import { useState, useEffect, useRef } from 'react';
import { fetchReportData, fetchSaleReportImages } from '../../../services/saleReport/saleReportService';
import legacy from '../../../utils/legacy';

function toPhotoUrls(key) {
  if (!key) return null;
  if (key.startsWith('http') || key.startsWith('blob:')) return { thumb: key, full: key, key };
  let base = legacy.server_by_hostname('aurora', 'files/sale_photo/');
  return {
    key,
    thumb: base + '380x200_' + key,
    full: base + key,
  };
}

async function fetchLandInfo(pnu) {
  if (!pnu) return null;
  try {
    const url = legacy.server_by_hostname('data', 'home/land_by_pnu/');
    const res = await fetch(`${url}?pnu=${encodeURIComponent(pnu)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function fetchLandPrice(pnu) {
  if (!pnu) return null;
  try {
    const url = legacy.server_by_hostname('data', 'home/land_price/');
    const res = await fetch(`${url}?pnu=${encodeURIComponent(pnu)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    const latest = data[data.length - 1];
    return { year: latest[0], price: latest[2] };
  } catch {
    return null;
  }
}

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 12; // 최대 60초

export default function useSaleReportData(saleUuid) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sale, setSale] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [agent, setAgent] = useState(null);
  const [reportImages, setReportImages] = useState({ map: null, cadastral: null, pending: [] });
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    if (!saleUuid) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchReportData(saleUuid);
        if (cancelled) return;
        if (res.status !== 'success') {
          setError(res.status || '데이터를 불러올 수 없습니다.');
          return;
        }

        const saleData = res.sale;

        // 토지 정보 및 공시지가 병렬 조회
        const [landInfo, landPrice] = await Promise.all([
          fetchLandInfo(saleData.pnu),
          fetchLandPrice(saleData.pnu),
        ]);
        if (!cancelled && landPrice) {
          saleData.official_land_price = landPrice.price;
          saleData.official_land_price_year = landPrice.year;
        }
        if (!cancelled && landInfo) {
          if (landInfo.p10) saleData.land_usage = landInfo.p10;
          if (landInfo.p12) saleData.land_height = landInfo.p12;
          if (landInfo.p14) saleData.land_shape = landInfo.p14;
          if (!saleData.land_purpose && landInfo.purpose) saleData.land_purpose_text = landInfo.purpose;
          if (!saleData.land_area && landInfo.area) saleData.land_area = landInfo.area;
          if (!saleData.entry_road && landInfo.p16) saleData.entry_road_text = landInfo.p16;
          if (!saleData.use_area && landInfo.p6) saleData.use_area_text = landInfo.p6;
          if (landInfo.land_use_planning_info_main) saleData.land_use_main = landInfo.land_use_planning_info_main;
          if (landInfo.land_use_planning_info_etc) saleData.land_use_etc = landInfo.land_use_planning_info_etc;
        }

        if (!cancelled) {
          setSale(saleData);
          setPhotos((res.photos || []).map(toPhotoUrls).filter(Boolean));
          setAgent(res.agent);
        }
      } catch (e) {
        if (!cancelled) setError('데이터를 불러올 수 없습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [saleUuid]);

  // 지도·지적도 이미지 — 첫 요청 후 pending 있으면 5초마다 폴링
  useEffect(() => {
    if (!saleUuid) return;

    let cancelled = false;
    pollAttemptsRef.current = 0;

    function clearPoll() {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    async function checkImages() {
      if (cancelled) return;
      try {
        const data = await fetchSaleReportImages(saleUuid);
        if (cancelled) return;

        const stillPending = (data.pending || []).filter(
          (t) => (t === 'map' && !data.map) || (t === 'cadastral' && !data.cadastral)
        );
        setReportImages(prev => ({
          map: prev.map || data.map || null,
          cadastral: prev.cadastral || data.cadastral || null,
          pending: stillPending,
        }));

        if (stillPending.length > 0 && pollAttemptsRef.current < POLL_MAX_ATTEMPTS) {
          pollAttemptsRef.current += 1;
          pollTimerRef.current = setTimeout(checkImages, POLL_INTERVAL_MS);
        } else if (stillPending.length > 0) {
          // 최대 시도 초과 → pending 해제
          setReportImages(prev => ({ ...prev, pending: [] }));
        }
      } catch {
        if (!cancelled) setReportImages(prev => ({ ...prev, pending: [] }));
      }
    }

    checkImages();

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [saleUuid]);

  return { loading, error, sale, photos, agent, reportImages };
}
